import { useState } from 'react';
import { XMarkIcon, GiftIcon, SparklesIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getTenantSlug } from '@/services/api';

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ReferralModal({ isOpen, onClose }: ReferralModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const slug = getTenantSlug();
  const referralUrl = `https://apps.tabillo.com/signup?ref=${slug}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-surface-default rounded-xl border border-border-default shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-default">
          <div className="flex items-center gap-2">
            <GiftIcon className="w-5 h-5 text-amber-500" />
            <h2 className="text-base font-semibold text-text-primary">Refer & Earn AI Credits</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-text-tertiary hover:text-text-secondary rounded-md transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Reward rows */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <SparklesIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">You earn +20 AI credits</p>
                <p className="text-xs text-amber-600 mt-0.5">When someone signs up using your referral link</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 border border-green-100">
              <GiftIcon className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">They get +10 AI credits</p>
                <p className="text-xs text-green-600 mt-0.5">A head-start for your referral's new workspace</p>
              </div>
            </div>
          </div>

          {/* Referral link */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1.5">Your referral link</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 text-sm bg-surface-subtle border border-border-default rounded-md text-text-secondary font-mono truncate">
                {referralUrl}
              </div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-sm font-medium rounded-md transition-colors shrink-0"
              >
                {copied ? (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <p className="text-xs text-text-tertiary">
            Credits are awarded automatically when a referred workspace is created and verified. No expiry on earned credits.
          </p>
        </div>
      </div>
    </div>
  );
}
